import React from "react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { formatNumber } from "shared/utils/numberFormatter";

function fmt(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return formatNumber(Number(n));
}

function fmtPct(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `${(Number(n) * 100).toFixed(1)}%`;
}

function fmtDur(sec) {
  if (sec == null || !Number.isFinite(Number(sec))) return "—";
  const s = Number(sec);
  if (s < 60) return `${Math.round(s)}s`;
  return `${(s / 60).toFixed(1)}m`;
}

function EmptyHint({ children }) {
  return (
    <div className="text-xs px-3 py-4 text-center" style={{ color: theme.textMuted }}>
      {children}
    </div>
  );
}

function KpiStrip({ items }) {
  return (
    <div className="flex flex-wrap gap-3 px-3 py-2 justify-between">
      {items.map((item) => (
        <div key={item.id || item.label} className="min-w-[100px] flex-1">
          <div className="text-[10px] uppercase tracking-wide" style={{ color: theme.textMuted }}>
            {item.label}
          </div>
          <div className="text-lg font-semibold tabular-nums" style={{ color: theme.textPrimary }}>
            {item.format ? item.format(item.value) : fmt(item.value)}
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniSeries({ series, valueKey = "sessions" }) {
  if (!series || series.length === 0) {
    return <EmptyHint>No session data in this range.</EmptyHint>;
  }
  const vals = series.map((p) => Number(p[valueKey]) || 0);
  const max = Math.max(1, ...vals);
  return (
    <div className="px-3 pb-2 flex items-end gap-0.5 h-16">
      {series.map((p, i) => {
        const v = Number(p[valueKey]) || 0;
        const h = Math.round((v / max) * 100);
        return (
          <div
            key={i}
            className="flex-1 min-w-[3px] rounded-sm"
            style={{
              height: `${Math.max(8, h)}%`,
              backgroundColor: theme.primaryLight,
              opacity: 0.5 + (v > 0 ? 0.5 * (v / max) : 0),
            }}
            title={`${p.day}: ${v}`}
          />
        );
      })}
    </div>
  );
}

function RankedList({ rows, labelKey, valueKey }) {
  if (!rows || rows.length === 0) {
    return <EmptyHint>No rows for this range.</EmptyHint>;
  }
  return (
    <ul className="px-3 pb-2 space-y-1 max-h-36 overflow-y-auto text-xs">
      {rows.slice(0, 8).map((row, i) => (
        <li key={i} className="flex justify-between gap-2">
          <span className="truncate" style={{ color: theme.textSecondary }}>
            {String(row[labelKey] ?? "")}
          </span>
          <span className="tabular-nums flex-shrink-0" style={{ color: theme.textPrimary }}>
            {fmt(row[valueKey])}
          </span>
        </li>
      ))}
    </ul>
  );
}

function renderByVariant(variant, data) {
  switch (variant) {
    case "team_pulse": {
      const kpis = data?.kpis || [];
      if (kpis.length === 0) return <EmptyHint>No overview data yet.</EmptyHint>;
      return <KpiStrip items={kpis} />;
    }
    case "agent_operations":
      return (
        <KpiStrip
          items={[
            { id: "c", label: "Conversations", value: data?.totalConversations },
            { id: "m", label: "Messages", value: data?.totalMessages },
            { id: "r", label: "Responses", value: data?.totalResponses },
            { id: "t", label: "Tool calls", value: data?.totalToolCalls },
            { id: "e", label: "Errors", value: data?.totalErrors },
            {
              id: "lat",
              label: "Avg tool latency",
              value: data?.avgToolLatencyMs,
              format: (v) => (v == null ? "—" : `${Math.round(v)} ms`),
            },
          ]}
        />
      );
    case "web_activity":
      return (
        <div>
          <div className="flex gap-4 px-3 pt-1 text-xs" style={{ color: theme.textSecondary }}>
            <span>
              Sessions: <strong style={{ color: theme.textPrimary }}>{fmt(data?.totalSessions)}</strong>
            </span>
            <span>
              Page views: <strong style={{ color: theme.textPrimary }}>{fmt(data?.pageViews)}</strong>
            </span>
          </div>
          <MiniSeries series={data?.series} />
        </div>
      );
    case "product_events":
      return (
        <div>
          <div className="px-3 pt-1 text-xs" style={{ color: theme.textSecondary }}>
            Total events:{" "}
            <strong style={{ color: theme.textPrimary }}>{fmt(data?.totalEvents)}</strong>
          </div>
          <RankedList rows={data?.topEvents} labelKey="eventName" valueKey="count" />
        </div>
      );
    case "session_engagement":
      return (
        <KpiStrip
          items={[
            { id: "s", label: "Sessions", value: data?.sessions },
            { id: "d", label: "Avg duration", value: data?.avgDurationSec, format: fmtDur },
            { id: "p", label: "Avg pages / session", value: data?.avgPagesPerSession, format: (v) => fmt(v) },
            { id: "b", label: "Bounce rate", value: data?.bounceRate, format: fmtPct },
            { id: "g", label: "Engaged sessions", value: data?.engagedSessions },
          ]}
        />
      );
    case "user_momentum":
      return (
        <KpiStrip
          items={[
            {
              id: "a",
              label: "Active users (distinct in sessions)",
              value: data?.activeUsersInRange,
            },
            {
              id: "n",
              label: "New users (first visit in range)",
              value: data?.newUsersInRange,
            },
          ]}
        />
      );
    case "quality_feedback":
      return (
        <KpiStrip
          items={[
            { id: "f", label: "Feedback rows", value: data?.totalFeedback },
            { id: "r", label: "Avg rating", value: data?.avgRating, format: (v) => (v == null ? "—" : v.toFixed(2)) },
            {
              id: "p",
              label: "Positive satisfaction",
              value: data?.positiveSatisfactionRate,
              format: fmtPct,
            },
            { id: "k", label: "Known satisfaction", value: data?.satisfactionKnownCount },
          ]}
        />
      );
    case "reliability_errors":
      return (
        <div>
          <div className="px-3 pt-1 text-xs" style={{ color: theme.textSecondary }}>
            Total errors:{" "}
            <strong style={{ color: theme.textPrimary }}>{fmt(data?.totalErrors)}</strong>
          </div>
          <RankedList rows={data?.topErrorTypes} labelKey="errorType" valueKey="count" />
        </div>
      );
    case "tool_performance":
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 px-3 pb-2 text-xs">
          <div>
            <div className="font-medium mb-1" style={{ color: theme.textMuted }}>
              Top tools
            </div>
            <RankedList rows={data?.topTools} labelKey="toolName" valueKey="callCount" />
          </div>
          <div>
            <div className="font-medium mb-1" style={{ color: theme.textMuted }}>
              Top failing tools
            </div>
            <RankedList rows={data?.topFailingTools} labelKey="toolName" valueKey="failureCount" />
          </div>
        </div>
      );
    case "workspace_health":
      return (
        <KpiStrip
          items={[
            { id: "s", label: "Sites connected", value: data?.sitesConnected },
            { id: "a", label: "Agents registered", value: data?.agentsRegistered },
            {
              id: "e",
              label: "Last product event",
              value: data?.lastEventAt,
              format: (v) => (v ? new Date(v).toLocaleString() : "—"),
            },
            {
              id: "m",
              label: "Last agent message",
              value: data?.lastAgentMessageAt,
              format: (v) => (v ? new Date(v).toLocaleString() : "—"),
            },
          ]}
        />
      );
    default:
      return <EmptyHint>Unknown widget variant.</EmptyHint>;
  }
}

/**
 * @param {{ card: object, result: object, rowHeightPx?: number }} props
 */
export default function TeamWidgetCard({ card, result }) {
  const err = result?.error;
  const inner = result?.data;
  const variant = inner?.variant || card?.visualization?.variant;
  const data = inner?.data;
  const meta = inner?.meta;

  if (err) {
    return (
      <div className="px-3 py-2 text-xs" style={{ color: "#c44848" }}>
        {String(err)}
      </div>
    );
  }

  if (!variant) {
    return <EmptyHint>Loading…</EmptyHint>;
  }

  return (
    <div className="flex flex-col min-h-0 h-full">
      {meta?.preset && (
        <div className="px-3 pt-1 text-[10px]" style={{ color: theme.textMuted }}>
          Range: {meta.preset}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-auto">{renderByVariant(variant, data)}</div>
    </div>
  );
}
